import React, { forwardRef, useImperativeHandle, useState, useEffect } from 'react';
import { ReactRenderer } from '@tiptap/react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';

// Dropdown list rendered at the cursor via a fixed-position portal.
export const MentionList = forwardRef(({ items, command }, ref) => {
  const [active, setActive] = useState(0);
  useEffect(() => { setActive(0); }, [items]);

  const select = (i) => {
    const item = items[i];
    if (!item) return;
    command({ id: item.user_id, label: item.name });
  };

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === 'ArrowDown') { setActive(i => (i + 1) % Math.max(items.length, 1)); return true; }
      if (event.key === 'ArrowUp')   { setActive(i => (i - 1 + items.length) % Math.max(items.length, 1)); return true; }
      if (event.key === 'Enter' || event.key === 'Tab') { select(active); return true; }
      return false;
    },
  }));

  if (!items.length) {
    return <div className="rounded-lg border border-zinc-200 bg-white shadow-lg py-2 px-3 text-[12px] text-zinc-400">No teammates</div>;
  }
  return (
    <div className="rounded-lg border border-zinc-200 bg-white shadow-lg py-1 w-56 max-h-64 overflow-y-auto" data-testid="mention-list">
      {items.map((m, i) => (
        <button key={m.user_id} type="button" onMouseDown={(e) => { e.preventDefault(); select(i); }}
          onMouseEnter={() => setActive(i)}
          data-testid={`mention-option-${m.user_id}`}
          className={`w-full flex items-center gap-2 px-2 py-1.5 text-left ${i === active ? 'bg-blue-50 text-blue-700' : 'hover:bg-zinc-50 text-zinc-700'}`}>
          <Avatar className="h-6 w-6"><AvatarImage src={m.avatar} /><AvatarFallback className="bg-gradient-to-br from-blue-400 to-indigo-500 text-white text-[9px] font-bold">{m.name?.charAt(0)?.toUpperCase()}</AvatarFallback></Avatar>
          <div className="flex-1 min-w-0">
            <div className="text-[12px] font-medium truncate">{m.name}</div>
            {m.email && <div className="text-[10px] text-zinc-400 truncate">{m.email}</div>}
          </div>
        </button>
      ))}
    </div>
  );
});
MentionList.displayName = 'MentionList';

// Factory returning a Mention extension 'suggestion' config bound to the
// caller-provided project member list. Items are filtered client-side.
export function makeMentionSuggestion(getMembers) {
  return {
    items: ({ query }) => {
      const members = (typeof getMembers === 'function' ? getMembers() : getMembers) || [];
      const q = (query || '').toLowerCase();
      return members
        .filter(m => m.user_id && m.name)
        .filter(m => !q || m.name.toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q))
        .slice(0, 6);
    },
    render: () => {
      let renderer;
      let wrapperEl;
      const positionAt = (clientRect) => {
        if (!wrapperEl || !clientRect) return;
        const r = clientRect();
        if (!r) return;
        wrapperEl.style.top = `${r.bottom + window.scrollY + 4}px`;
        wrapperEl.style.left = `${r.left + window.scrollX}px`;
      };
      return {
        onStart: (props) => {
          renderer = new ReactRenderer(MentionList, { props, editor: props.editor });
          wrapperEl = document.createElement('div');
          wrapperEl.style.position = 'absolute';
          wrapperEl.style.zIndex = '1000';
          // CRITICAL: Radix Sheet/Dialog sets `pointer-events: none` on
          // <body> while open (their dismissable-layer trick). Because
          // pointer-events INHERITS, every descendant — including our
          // mention dropdown — silently swallows clicks unless we opt
          // back in explicitly. Without this the user sees the list but
          // can't select a teammate.
          wrapperEl.style.pointerEvents = 'auto';
          // Stop pointer/mouse events at the wrapper so Radix Dialog (used by
          // the TaskSheet) doesn't treat clicks on the suggestion list as
          // "outside" interactions and steal focus from the editor.
          const stop = (e) => e.stopPropagation();
          wrapperEl.addEventListener('mousedown', stop);
          wrapperEl.addEventListener('pointerdown', stop);
          wrapperEl.addEventListener('touchstart', stop);
          wrapperEl.appendChild(renderer.element);
          document.body.appendChild(wrapperEl);
          positionAt(props.clientRect);
        },
        onUpdate: (props) => {
          renderer?.updateProps(props);
          positionAt(props.clientRect);
        },
        onKeyDown: (props) => renderer?.ref?.onKeyDown?.(props) || false,
        onExit: () => {
          renderer?.destroy();
          if (wrapperEl && wrapperEl.parentNode) wrapperEl.parentNode.removeChild(wrapperEl);
          wrapperEl = null;
        },
      };
    },
  };
}
